<template>
  <div class="tasks-board">
    <div class="tasks-board__header">
      <div class="tasks-board__title-row">
        <h1 class="tasks-board__title">Tasks</h1>
        <span class="tasks-board__count">{{ tasks.length }}</span>
      </div>
    </div>

    <skeleton-tasks v-if="isLoading"></skeleton-tasks>

    <div v-else class="tasks-board__columns">
      <div v-for="column in columns" :key="column.key" class="tasks-board__column">
        <div class="tasks-board__column-header">
          <span class="tasks-board__column-dot" :class="'tasks-board__column-dot--' + column.key"></span>
          <span class="tasks-board__column-name">{{ column.label }}</span>
          <span class="tasks-board__column-count">{{ getColumnTasks(column.key).length }}</span>
        </div>

        <div class="tasks-board__column-body">
          <div v-if="getColumnTasks(column.key).length === 0" class="tasks-board__empty">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            <span>No tasks</span>
          </div>
          <task
            v-for="(task, index) in getColumnTasks(column.key)"
            :key="index"
            :task="task">
          </task>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonTasks from '@/components/UIComponents/SkeletonTasks'
import { mapState } from 'vuex'
import Task from './Includes/SingleTaskCard'

export default {
  name: 'all-tasks',
  components: {
    task: Task,
    SkeletonTasks
  },
  data () {
    return {
      isLoading: false,
      columns: [
        { key: 'created', label: 'Todo' },
        { key: 'collecting', label: 'Data requested' },
        { key: 'inProgress', label: 'In progress' },
        { key: 'toCheck', label: 'To check' },
        { key: 'complete', label: 'Ready' },
        { key: 'sentToApplicant', label: 'Sent to applicant' }
      ]
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      applicants: state => state.applicants.applicants,
      managers: state => state.managers.managers
    }),
    filteredApplicants () {
      if (this.user && this.user.isManager && !this.user.isAdmin) {
        return this.applicants.filter(applicant => {
          return applicant.availableTo && applicant.availableTo.includes(this.user.firstName.toLowerCase())
        })
      }
      return this.applicants || []
    },
    activeApplicants () {
      return this.filteredApplicants.filter(applicant => applicant.isActive)
    },
    tasks () {
      return this.activeApplicants.reduce((taskList, applicant) => {
        if (!applicant.requirements) return taskList
        return taskList.concat(applicant.requirements.map(task => {
          return Object.assign({}, task, {
            applicant: applicant.firstName + ' ' + applicant.lastName
          })
        }))
      }, [])
    }
  },
  methods: {
    getColumnTasks (status) {
      return this.tasks.filter(task => task.status === status)
    }
  },
  mounted () {
  },
  created () {
    this.isLoading = true
    this.$store.dispatch('GET_USER')
    this.$store.dispatch('GET_APPLICANTS').then(() => {
      this.isLoading = false
    }).catch(() => {
      this.isLoading = false
    })

    if (this.user && !this.user.isAdmin && !this.user.isManager) {
      this.$router.push({ name: 'components' })
    }
  }
}
</script>

<style scoped>
.tasks-board__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.tasks-board__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tasks-board__title {
  font-size: 26px;
  font-weight: 700;
  color: #131415;
  letter-spacing: -0.03em;
  margin: 0;
}

.tasks-board__count {
  background: #f0eef5;
  color: #7c5bea;
  font-size: 13px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
}

/* Kanban columns */
.tasks-board__columns {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  min-height: calc(100vh - 140px);
}

@media (max-width: 768px) {
  .tasks-board__columns {
    grid-template-columns: repeat(6, minmax(200px, 1fr));
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 12px;
    scroll-snap-type: x mandatory;
  }

  .tasks-board__column {
    scroll-snap-align: start;
  }
}

.tasks-board__column {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tasks-board__column-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 12px;
}

.tasks-board__column-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tasks-board__column-dot--created {
  background: #d6d8e3;
}

.tasks-board__column-dot--collecting {
  background: #d4a054;
}

.tasks-board__column-dot--inProgress {
  background: #3AA6DD;
}

.tasks-board__column-dot--toCheck {
  background: #7c5bea;
}

.tasks-board__column-dot--complete {
  background: #2d7a0e;
}

.tasks-board__column-dot--sentToApplicant {
  background: #1a8a6e;
}

.tasks-board__column-name {
  font-size: 12px;
  font-weight: 600;
  color: #131415;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tasks-board__column-count {
  font-size: 12px;
  font-weight: 500;
  color: #a8a3ac;
}

.tasks-board__column-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  padding: 12px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.tasks-board__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #c0bcc4;
  padding: 24px 4px;
}

.tasks-board__empty span {
  color: #b5b0b8;
}
</style>
